import { Inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { Gruppe } from '../classes/gruppe';
import { concatMap, from, last, Observable, of, Subject, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { Haendler } from '../../stammdaten/haendler/haendler';
import { EinstallHerkunftFilter, ScanErgebnis } from '../classes/scan-ergebnis';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { mergeScanErgebnisse } from '../../../utils/ohrmarke-pass-scan.util';

@Injectable({ providedIn: 'root' })
export class EinstallenService {
    private readonly maxLastScans = 500;
    currentGruppeSubject = new Subject<Gruppe>();
    currentGruppe: Gruppe;

    eingestallteTiereSubject = new Subject<void>();

    currentAbteilSubject = new Subject<string>();
    currentAbteil: string;

    currentHaendlerSubject = new Subject<Haendler>();

    scanErgebnisSubject = new Subject<ScanErgebnis>();
    private einstallTierlisteIdSubject = new Subject<number | null>();
    gescannteOhrmarken: ScanErgebnis[] = [];
    private gescannteOhrmarkenSubject = new Subject<ScanErgebnis[]>();
    private pendingGewichte = new Map<number, number>();
    private bereitsInGruppeGtIds = new Set<number>();
    private failedImportScans: ScanErgebnis[] = [];
    private linkedExistingScans: ScanErgebnis[] = [];
    private currentEinstalldatum: Date | null = null;
    private currentEinstallTierlisteId: number | null = null;

    constructor(
        private readonly api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {}

    loadGruppe(gruppe_id: number) {
        const params = new HttpParams().set('gruppe_id', gruppe_id.toString());
        return this.api.get(this.apiBaseUrl + 'gruppe/read_one_kundenportal.php', params);
    }

    setCurrentGruppe(gruppe: Gruppe) {
        this.currentGruppe = gruppe;
        this.currentGruppeSubject.next(this.currentGruppe);
    }

    getCurrentGruppe(): Gruppe {
        return this.currentGruppe;
    }

    watchCurrentGruppe() {
        return this.currentGruppeSubject.asObservable();
    }

    ladeEingestallteTiere() {
        this.eingestallteTiereSubject.next();
    }

    watchEingestallteTiere() {
        return this.eingestallteTiereSubject.asObservable();
    }

    setCurrentAbteil(currentAbteil: string) {
        this.currentAbteil = currentAbteil;
        this.currentAbteilSubject.next(this.currentAbteil);
    }

    getCurrentAbteil(): string | null {
        return this.resolveAbteil(this.currentAbteil);
    }

    resolveAbteil(value: string | null | undefined): string | null {
        const raw = value ?? this.currentAbteil;
        if (raw == null) {
            return null;
        }
        const trimmed = String(raw).trim();
        return trimmed === '' ? null : trimmed;
    }

    watchCurrentAbteil() {
        return this.currentAbteilSubject.asObservable();
    }

    private currentBetriebskontaktHaendlerId: number | null = null;
    private currentHaendler: Haendler | null = null;

    setCurrentHaendler(haendler: Haendler | null) {
        this.currentHaendler = haendler;
        this.currentHaendlerSubject.next(haendler);
    }

    getCurrentHaendler(): Haendler | null {
        return this.currentHaendler;
    }

    watchCurrentHaendler() {
        return this.currentHaendlerSubject.asObservable();
    }

    setCurrentBetriebskontaktHaendlerId(kundeId: number | null): void {
        const id = Number(kundeId || 0);
        this.currentBetriebskontaktHaendlerId = id > 0 ? id : null;
    }

    getCurrentBetriebskontaktHaendlerId(): number | null {
        return this.currentBetriebskontaktHaendlerId;
    }

    clearCurrentHerkunft(): void {
        this.currentBetriebskontaktHaendlerId = null;
        this.setCurrentHaendler(null);
    }

    setScanErgebnisSubject(scanErgebnis: ScanErgebnis) {
        this.scanErgebnisSubject.next(scanErgebnis);
    }

    watchScanErgebnisSubject() {
        return this.scanErgebnisSubject.asObservable();
    }

    addScanErgebnis(ergebnis: ScanErgebnis): void {
        const scan = Object.assign(new ScanErgebnis(), ergebnis);
        this.applyBereitsInGruppeFlag(scan);
        if (scan.bereitsInGruppe) {
            this.registerLinkedExistingScans([scan]);
        }

        const gtId = Number(scan.gt_id || 0);
        if (gtId > 0) {
            const existingIndex = this.gescannteOhrmarken.findIndex(item => Number(item?.gt_id) === gtId);
            if (existingIndex >= 0) {
                const merged = mergeScanErgebnisse(this.gescannteOhrmarken[existingIndex], scan);
                this.applyBereitsInGruppeFlag(merged);
                this.gescannteOhrmarken.splice(existingIndex, 1);
                this.gescannteOhrmarken.unshift(merged);
                if (this.gescannteOhrmarken.length > this.maxLastScans) {
                    this.gescannteOhrmarken.splice(this.maxLastScans);
                }
                this.gescannteOhrmarkenSubject.next([...this.gescannteOhrmarken]);
                return;
            }
        }

        this.gescannteOhrmarken.unshift(scan);
        if (this.gescannteOhrmarken.length > this.maxLastScans) {
            this.gescannteOhrmarken.splice(this.maxLastScans);
        }
        this.gescannteOhrmarkenSubject.next([...this.gescannteOhrmarken]);
    }

    getLastScanErgebnisse(): ScanErgebnis[] {
        return [...this.gescannteOhrmarken];
    }

    watchLastScanErgebnisse(): Observable<ScanErgebnis[]> {
        return this.gescannteOhrmarkenSubject.asObservable();
    }

    clearLastScanErgebnisse(): void {
        this.gescannteOhrmarken = [];
        this.clearLinkedExistingScans();
        this.gescannteOhrmarkenSubject.next([]);
    }

    registerLinkedExistingScans(scans: ScanErgebnis[]): void {
        for (const scan of scans || []) {
            if (!scan?.gt_id && !scan?.ohrmarke_notnull) {
                continue;
            }
            const entry = Object.assign(new ScanErgebnis(), scan, {
                success: true,
                bereitsInGruppe: true,
                importFailed: false,
            });
            this.applyBereitsInGruppeFlag(entry);
            if (entry.gt_id) {
                this.bereitsInGruppeGtIds.add(Number(entry.gt_id));
            }
            const gtId = Number(entry.gt_id || 0);
            const ohrmarke = String(entry.ohrmarke_notnull || '').trim();
            const existingIndex = this.linkedExistingScans.findIndex(item => {
                if (gtId > 0 && Number(item?.gt_id) === gtId) {
                    return true;
                }
                return ohrmarke !== '' && String(item?.ohrmarke_notnull || '').trim() === ohrmarke;
            });
            if (existingIndex >= 0) {
                this.linkedExistingScans[existingIndex] = entry;
            } else {
                this.linkedExistingScans.push(entry);
            }
        }
        if (this.linkedExistingScans.length > this.maxLastScans) {
            this.linkedExistingScans.splice(this.maxLastScans);
        }
    }

    getLinkedExistingScans(): ScanErgebnis[] {
        return this.linkedExistingScans.map(scan =>
            Object.assign(new ScanErgebnis(), scan, {
                success: true,
                bereitsInGruppe: true,
                importFailed: false,
            }),
        );
    }

    clearLinkedExistingScans(): void {
        this.linkedExistingScans = [];
    }

    registerBereitsInGruppeGtIds(gtIds: number[]): void {
        for (const gtId of gtIds || []) {
            const id = Number(gtId);
            if (id > 0) {
                this.bereitsInGruppeGtIds.add(id);
            }
        }
    }

    clearBereitsInGruppeGtIds(): void {
        this.bereitsInGruppeGtIds.clear();
        this.clearLinkedExistingScans();
    }

    registerFailedImportScans(scans: ScanErgebnis[]): void {
        for (const scan of scans || []) {
            if (!scan || (scan.success !== false && !scan.importFailed)) {
                continue;
            }
            const ohrmarke = String(scan.ohrmarke_notnull || '').trim();
            if (!ohrmarke) {
                continue;
            }
            const entry = Object.assign(new ScanErgebnis(), scan, {
                success: false,
                importFailed: true,
                ohrmarke_notnull: ohrmarke,
            });
            const existingIndex = this.failedImportScans.findIndex(
                item => String(item.ohrmarke_notnull || '').trim() === ohrmarke,
            );
            if (existingIndex >= 0) {
                this.failedImportScans[existingIndex] = entry;
            } else {
                this.failedImportScans.push(entry);
            }
        }
        if (this.failedImportScans.length > this.maxLastScans) {
            this.failedImportScans.splice(this.maxLastScans);
        }
    }

    getFailedImportScans(): ScanErgebnis[] {
        return this.failedImportScans.map(scan =>
            Object.assign(new ScanErgebnis(), scan, { success: false, importFailed: true }),
        );
    }

    clearFailedImportScans(): void {
        this.failedImportScans = [];
    }

    removeFailedImportScansForOhrmarken(ohrmarken: string[]): void {
        const successOhrmarken = new Set(
            (ohrmarken || []).map(ohrmarke => String(ohrmarke || '').trim()).filter(ohrmarke => !!ohrmarke),
        );
        if (successOhrmarken.size === 0) {
            return;
        }
        this.failedImportScans = this.failedImportScans.filter(
            scan => !successOhrmarken.has(String(scan.ohrmarke_notnull || '').trim()),
        );
    }

    isBereitsInGruppeGtId(gtId: number): boolean {
        return this.bereitsInGruppeGtIds.has(Number(gtId));
    }

    private applyBereitsInGruppeFlag(scan: ScanErgebnis): ScanErgebnis {
        if (scan?.gt_id && (scan.bereitsInGruppe || this.isBereitsInGruppeGtId(scan.gt_id))) {
            scan.bereitsInGruppe = true;
        }
        return scan;
    }

    setLastScanErgebnisse(scans: ScanErgebnis[]): void {
        const gewichtByGtId = this.buildPreservedGewichtLookup();
        const apiScans = (scans || []).map(scan => {
            const merged = Object.assign(new ScanErgebnis(), scan);
            merged.success = true;
            merged.importFailed = false;
            this.applyBereitsInGruppeFlag(merged);
            const preservedGewicht = gewichtByGtId.get(merged.gt_id);
            if (!merged.bereitsInGruppe && preservedGewicht != null && preservedGewicht > 0) {
                merged.einstallgewicht = preservedGewicht;
            }
            return merged;
        });
        const successOhrmarken = new Set(
            apiScans.map(scan => String(scan.ohrmarke_notnull || '').trim()).filter(ohrmarke => !!ohrmarke),
        );
        const apiGtIds = new Set(
            apiScans.map(scan => Number(scan?.gt_id)).filter(gtId => gtId > 0),
        );
        const failedScans = this.getFailedImportScans().filter(
            scan => !successOhrmarken.has(String(scan.ohrmarke_notnull || '').trim()),
        );
        const linkedExistingScans = this.getLinkedExistingScans().filter(scan => {
            const gtId = Number(scan?.gt_id || 0);
            if (gtId > 0 && apiGtIds.has(gtId)) {
                return false;
            }
            const ohrmarke = String(scan.ohrmarke_notnull || '').trim();
            return !(ohrmarke && successOhrmarken.has(ohrmarke));
        });
        this.gescannteOhrmarken = [...linkedExistingScans, ...apiScans, ...failedScans];
        this.gescannteOhrmarkenSubject.next([...this.gescannteOhrmarken]);
    }

    /** Review/melden: tierliste animals only (no linked-existing warnings in session memory). */
    setTierlisteScanErgebnisse(scans: ScanErgebnis[]): void {
        const gewichtByGtId = this.buildPreservedGewichtLookup();
        const apiScans = (scans || []).map(scan => {
            const merged = Object.assign(new ScanErgebnis(), scan);
            merged.success = true;
            merged.importFailed = false;
            merged.bereitsInGruppe = false;
            const preservedGewicht = gewichtByGtId.get(merged.gt_id);
            if (preservedGewicht != null && preservedGewicht > 0) {
                merged.einstallgewicht = preservedGewicht;
            }
            return merged;
        }).filter(scan => !!scan?.gt_id);
        const successOhrmarken = new Set(
            apiScans.map(scan => String(scan.ohrmarke_notnull || '').trim()).filter(ohrmarke => !!ohrmarke),
        );
        const failedScans = this.getFailedImportScans().filter(
            scan => !successOhrmarken.has(String(scan.ohrmarke_notnull || '').trim()),
        );
        this.gescannteOhrmarken = [...apiScans, ...failedScans];
        this.gescannteOhrmarkenSubject.next([...this.gescannteOhrmarken]);
    }

    removeScanErgebnis(gt_id: number): void {
        if (!gt_id) {
            return;
        }
        this.gescannteOhrmarken = this.gescannteOhrmarken.filter(scan => scan?.gt_id !== gt_id);
        this.removePendingGewicht(gt_id);
        this.gescannteOhrmarkenSubject.next([...this.gescannteOhrmarken]);
    }

    /** Pending / in-memory weights survive tierliste reloads (e.g. stepper step back). */
    private buildPreservedGewichtLookup(): Map<number, number> {
        const gewichtByGtId = new Map<number, number>();
        for (const scan of this.gescannteOhrmarken) {
            const gewicht = Number(scan?.einstallgewicht);
            if (scan?.gt_id && gewicht > 0) {
                gewichtByGtId.set(scan.gt_id, gewicht);
            }
        }
        for (const [gtId, gewicht] of this.pendingGewichte) {
            if (gtId && gewicht > 0) {
                gewichtByGtId.set(gtId, gewicht);
            }
        }
        return gewichtByGtId;
    }

    setCurrentEinstalldatum(datum: Date | null): void {
        this.currentEinstalldatum = datum ? new Date(datum) : null;
    }

    getCurrentEinstalldatum(): Date | null {
        return this.currentEinstalldatum ? new Date(this.currentEinstalldatum) : null;
    }

    clearCurrentEinstalldatum(): void {
        this.currentEinstalldatum = null;
    }

    setCurrentEinstallTierlisteId(tierlisteId: number | null): void {
        const id = Number(tierlisteId || 0);
        this.currentEinstallTierlisteId = id > 0 ? id : null;
        this.einstallTierlisteIdSubject.next(this.currentEinstallTierlisteId);
    }

    getCurrentEinstallTierlisteId(): number | null {
        return this.currentEinstallTierlisteId;
    }

    watchCurrentEinstallTierlisteId() {
        return this.einstallTierlisteIdSubject.asObservable();
    }

    clearCurrentEinstallTierlisteId(): void {
        this.currentEinstallTierlisteId = null;
        this.einstallTierlisteIdSubject.next(null);
    }

    loadEinstallTierlisteAnzahl(
        gruppeId: number,
        einstallTierlisteId: number,
        filter?: EinstallHerkunftFilter | null,
    ): Observable<number> {
        let params = new HttpParams()
            .set('einstall_tierliste_id', String(einstallTierlisteId))
            .set('gruppe_id', String(gruppeId));
        params = this.appendHerkunftParams(params, filter);
        return this.api
            .get(this.apiBaseUrl + 'gruppe/read_einstall_tierliste_anzahl.php', params)
            .pipe(map((res: { anzahl_tiere?: number }) => Math.max(0, Number(res?.anzahl_tiere ?? 0))));
    }

    loadEinstallTierlisteTiere(
        gruppeId: number,
        einstallTierlisteId: number,
        filter?: EinstallHerkunftFilter | null,
    ): Observable<ScanErgebnis[]> {
        let params = new HttpParams()
            .set('gruppe_id', String(gruppeId))
            .set('einstall_tierliste_id', String(einstallTierlisteId))
            .set('for_continue_scan', '1');
        params = this.appendHerkunftParams(params, filter);
        return this.api.getAll(this.apiBaseUrl + 'gruppe/read_tiere_fuer_letzte_aktion.php', params).pipe(
            map(response => this.mapTierlisteScanRecords(response?.records || [])),
        );
    }

    mapTierlisteScanRecords(records: unknown[]): ScanErgebnis[] {
        if (!Array.isArray(records)) {
            return [];
        }
        return records
            .filter((record): record is Record<string, unknown> => !!record && typeof record === 'object')
            .filter(record => Number(record['gt_id']) > 0)
            .map(record => {
                const scan = new ScanErgebnis();
                scan.gt_id = Number(record['gt_id']);
                const ohrmarke = String(record['ohrmarke_notnull'] || '').trim();
                scan.ohrmarke_notnull = ohrmarke || `GT-${scan.gt_id}`;
                scan.ohrmarke_gescannt = Number(record['ohrmarke_gescannt'] || 0);
                scan.pass_gescannt = Number(record['pass_gescannt'] || 0);
                scan.abteil = record['abteil'] != null ? String(record['abteil']) : undefined;
                scan.einstallgewicht =
                    record['einstallgewicht'] != null && record['einstallgewicht'] !== ''
                        ? Number(record['einstallgewicht'])
                        : undefined;
                scan.rasse = record['rasse'] != null ? String(record['rasse']) : undefined;
                scan.geschlecht = record['geschlecht'] != null ? String(record['geschlecht']) : undefined;
                scan.geschlecht_id =
                    record['geschlecht_id'] != null && record['geschlecht_id'] !== ''
                        ? Number(record['geschlecht_id'])
                        : undefined;
                scan.viehhaendler_id =
                    record['viehhaendler_id'] != null && record['viehhaendler_id'] !== ''
                        ? Number(record['viehhaendler_id'])
                        : undefined;
                scan.viehhaendler_name =
                    record['viehhaendler_name'] != null ? String(record['viehhaendler_name']).trim() : undefined;
                scan.betriebskontakt_haendler_id =
                    record['betriebskontakt_haendler_id'] != null && record['betriebskontakt_haendler_id'] !== ''
                        ? Number(record['betriebskontakt_haendler_id'])
                        : undefined;
                scan.betriebskontakt_haendler_name =
                    record['betriebskontakt_haendler_name'] != null
                        ? String(record['betriebskontakt_haendler_name']).trim()
                        : undefined;
                scan.success = true;
                this.applyBereitsInGruppeFlag(scan);
                return scan;
            });
    }

    private appendHerkunftParams(params: HttpParams, filter?: EinstallHerkunftFilter | null): HttpParams {
        if (filter?.viehhaendlerId && filter.viehhaendlerId > 0) {
            params = params.set('viehhaendler_id', String(filter.viehhaendlerId));
        }
        if (filter?.betriebskontaktHaendlerId && filter.betriebskontaktHaendlerId > 0) {
            params = params.set('betriebskontakt_haendler_id', String(filter.betriebskontaktHaendlerId));
        }
        return params;
    }

    createEinstallTierliste(gruppeId: number, kundeId?: number | null): Observable<{ tierliste_id?: number }> {
        const payload: Record<string, unknown> = {
            gruppe_id_einstallen: gruppeId,
        };
        if (kundeId && kundeId > 0) {
            payload.kunde_id_einstallen = kundeId;
        }
        return this.api.post(this.apiBaseUrl + 'tierliste/create_einstall_kundenportal.php', payload, undefined);
    }

    ensureEinstallTierliste(gruppeId: number, kundeId?: number | null, forceNew = false): Observable<number> {
        if (!gruppeId || gruppeId <= 0) {
            return throwError(() => new Error('Gruppe fehlt.'));
        }

        const cachedTierlisteId = this.getCurrentEinstallTierlisteId();
        const cachedGruppeId = Number(this.currentGruppe?.gruppe_id || 0);
        if (!forceNew && cachedTierlisteId && cachedGruppeId === gruppeId) {
            return of(cachedTierlisteId);
        }

        if (forceNew) {
            this.clearCurrentEinstallTierlisteId();
            this.clearBereitsInGruppeGtIds();
            this.clearFailedImportScans();
        }

        return this.createEinstallTierliste(gruppeId, kundeId).pipe(
            map(res => Number(res?.tierliste_id || 0)),
            switchMap(tierlisteId => {
                if (tierlisteId <= 0) {
                    return throwError(() => new Error('Einstall-Tierliste konnte nicht erstellt werden.'));
                }
                return of(tierlisteId);
            }),
            tap(tierlisteId => this.setCurrentEinstallTierlisteId(tierlisteId)),
        );
    }

    setPendingGewicht(gt_id: number, gewicht: number): void {
        if (!gt_id || !gewicht || gewicht <= 0) {
            return;
        }
        this.pendingGewichte.set(gt_id, gewicht);
        for (const scan of this.gescannteOhrmarken) {
            if (scan?.gt_id === gt_id) {
                scan.einstallgewicht = gewicht;
                break;
            }
        }
    }

    getPendingGewichte(): Array<{ gt_id: number; gewicht: number }> {
        return Array.from(this.pendingGewichte.entries()).map(([gt_id, gewicht]) => ({ gt_id, gewicht }));
    }

    getPendingGewichtForTier(gt_id: number): number | undefined {
        return this.pendingGewichte.get(gt_id);
    }

    removePendingGewicht(gt_id: number): void {
        if (!gt_id) {
            return;
        }
        this.pendingGewichte.delete(gt_id);
    }

    getPendingGewichteForActiveScans(): Array<{ gt_id: number; gewicht: number }> {
        const activeGtIds = new Set(
            this.gescannteOhrmarken
                .filter(scan => !!scan?.success && !!scan?.gt_id && !scan?.bereitsInGruppe && !scan?.importFailed)
                .map(scan => Number(scan.gt_id))
                .filter(id => id > 0),
        );
        if (activeGtIds.size === 0) {
            return [];
        }
        return this.getPendingGewichte().filter(entry => activeGtIds.has(entry.gt_id));
    }

    clearPendingGewichte(): void {
        this.pendingGewichte.clear();
    }

    /** Persist per-animal Einstallgewichte immediately (survives Scans fortsetzen / step navigation). */
    persistEinstallgewichte(
        entries: Array<{ gt_id: number; gewicht: number }>,
        gsId?: number | null,
    ): Observable<void> {
        const resolvedGsId = Number(gsId || this.currentGruppe?.gs_id || 0);
        if (resolvedGsId <= 0) {
            return throwError(() => new Error('Gruppenstandort (gs_id) fehlt.'));
        }

        const valid = entries.filter(entry => entry?.gt_id > 0 && entry?.gewicht > 0);
        if (valid.length === 0) {
            return of(undefined);
        }

        return from(valid).pipe(
            concatMap(entry => {
                const payload = {
                    gs_id: resolvedGsId,
                    gt_id: entry.gt_id,
                    einstallgewicht: entry.gewicht,
                };
                return this.api.post(this.apiBaseUrl + 'gruppe_standort_tier/update.php', payload, new HttpParams());
            }),
            last(),
            map(() => undefined),
        );
    }
}
